import { Select, useComputedColorScheme } from '@mantine/core';
import { Provider } from 'interfaces';
import { useActiveForm } from 'pages/claims/components';
import React, { useEffect, useMemo } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { getValueByPath } from 'utils/getValueByPath';

interface ModalProviderGroupSelectProps {
  disabled?: boolean;
}

export const ModalProviderGroupSelect: React.FC<
  ModalProviderGroupSelectProps
> = ({ disabled = false }) => {
  const { id: siteID } = useCurrentSiteStore();
  const { form, prefix } = useActiveForm();
  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';

  const providerPath = prefix ? `${prefix}.provider` : 'provider';
  const claimGroupIDPath = prefix ? `${prefix}.groupID` : 'groupID';
  const claimGroupPath = prefix ? `${prefix}.group` : 'group';

  const provider: Provider | undefined = getValueByPath(
    form.getValues(),
    providerPath
  );
  const providerGroups = useMemo(
    () => provider?.groups.filter((group) => group.siteID === siteID) ?? [],
    [provider, siteID]
  );

  const data = useMemo(
    () =>
      providerGroups.map((g) => ({
        value: g.id,
        label: g.name,
      })),
    [providerGroups]
  );

  const rawValue: string | null =
    getValueByPath(form.getValues(), claimGroupIDPath) || null;
  const rawGroup = getValueByPath(form.getValues(), claimGroupPath) || null;

  const value = rawValue ?? null;

  useEffect(() => {
    if (
      providerGroups.length === 0 &&
      (rawValue !== null || rawGroup !== null)
    ) {
      form.setFieldValue(claimGroupIDPath, null);
      form.setFieldValue(claimGroupPath, null);
    }
  }, [
    claimGroupIDPath,
    claimGroupPath,
    form,
    providerGroups.length,
    rawGroup,
    rawValue,
  ]);

  const handleChange = (newVal: string | null) => {
    const group = providerGroups.find((g) => g.id === newVal) || null;
    form.setFieldValue(claimGroupIDPath, newVal);
    form.setFieldValue(claimGroupPath, group);
  };

  return (
    <React.Fragment key={claimGroupIDPath}>
      <Select
        key={claimGroupIDPath}
        radius="md"
        label={'Group'}
        c={color}
        size="md"
        placeholder="Select an option"
        clearable
        searchable
        required
        allowDeselect
        data={data}
        value={value}
        onChange={handleChange}
        nothingFoundMessage="No groups found"
        disabled={disabled}
      />
    </React.Fragment>
  );
};
